'use client';

/// <reference types="@types/google.maps" />

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

// Define minimal shapes to interop old/new Places objects without tight coupling
type LegacyPlaceResult = google.maps.places.PlaceResult;
type NewPlace = any; // google.maps.places.Place (new data model)
type AutocompleteSuggestionType = any; // google.maps.places.AutocompleteSuggestion
type AutocompleteSessionTokenType = any; // google.maps.places.AutocompleteSessionToken

interface AddressFormProps {
  onSubmit: ({ 
    streetName,
    houseNumber, 
    zipCode, 
    city, 
    propertyType,
    latitude,
    longitude
  }: { 
    streetName: string;
    houseNumber: string;
    zipCode: string; 
    city: string; 
    propertyType: 'apartment' | 'house';
    latitude: number;
    longitude: number;
  }) => void;
  onAddressError?: (error: {
    code: 'geocode_failed' | 'missing_coordinates' | 'postal_code_unresolved' | 'house_number_unresolved' | 'country_not_supported';
    message: string;
    details?: Record<string, any>;
  }) => void;
  dictionary: any;
  disableSubmit?: boolean;
  // Prefill support for session-resume
  initialStreetName?: string;
  initialHouseNumber?: string;
  initialZipCode?: string;
  initialCity?: string;
  initialLatitude?: number;
  initialLongitude?: number;
  initialPropertyType?: 'apartment' | 'house';
  autoOpen?: boolean;
  onOpen?: () => void;
}


export function AddressForm({ onSubmit, onAddressError, dictionary, disableSubmit = false, initialStreetName, initialHouseNumber, initialZipCode, initialCity, initialLatitude, initialLongitude, initialPropertyType, autoOpen = false, onOpen }: AddressFormProps) {
  const [propertyType, setPropertyType] = useState<'apartment' | 'house'>('apartment');
  const [showForm, setShowForm] = useState<boolean>(false);
  // Autocomplete (new Places predictions API)
  const [placesLibLoaded, setPlacesLibLoaded] = useState<boolean>(false);
  const placesLibRef = useRef<{ Place: any; AutocompleteSessionToken: any; AutocompleteSuggestion: any } | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<LegacyPlaceResult | NewPlace | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  // Store our extracted/fixed components separately since place object may be incomplete/incorrect
  const [extractedData, setExtractedData] = useState<{ 
    houseNumber: string; 
    zipCode: string;
    latitude: number;
    longitude: number;
    countryCode?: string | null;
  } | null>(null);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestionType[]>([]);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [sessionToken, setSessionToken] = useState<AutocompleteSessionTokenType | null>(null);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const didOpenRef = useRef<boolean>(false);
  
  const [errors, setErrors] = useState<{
    address?: string; // Single error key for address input
    propertyType?: string; 
  }>({});

  const logDebug = useCallback((message: string, payload?: unknown) => {
    if (payload !== undefined) {
      console.debug(`[AddressForm] ${message}`, payload);
    } else {
      console.debug(`[AddressForm] ${message}`);
    }
  }, []);

  const showAddressError = useCallback((message: string) => {
    setErrors({ address: message });
    setSuggestions([]);
    setHighlightedIndex(-1);
  }, []);

  // Initialize from prefilled values (session-resume)
  useEffect(() => {
    try {
      if (initialPropertyType) {
        setPropertyType(initialPropertyType);
      }
      const hasInitialAddress = Boolean(initialStreetName && initialHouseNumber && initialZipCode && initialCity && typeof initialLatitude === 'number' && typeof initialLongitude === 'number');
      if (autoOpen || hasInitialAddress) {
        setShowForm(true);
      }
      if (hasInitialAddress) {
        // Compose a minimal Place-like object so extractAddressComponents works
        const placeLike: any = {
          address_components: [
            { long_name: String(initialHouseNumber), types: ['street_number'] },
            { long_name: String(initialStreetName), types: ['route'] },
            { long_name: String(initialZipCode), types: ['postal_code'] },
            { long_name: String(initialCity), types: ['locality'] },
            { long_name: 'Belgium', short_name: 'BE', types: ['country', 'political'] },
          ],
          geometry: {
            location: {
              lat: () => Number(initialLatitude),
              lng: () => Number(initialLongitude),
            }
          },
          formattedAddress: `${initialStreetName} ${initialHouseNumber}, ${initialZipCode} ${initialCity}`
        };
        setSelectedPlace(placeLike);
        setAddressInputValue(placeLike.formattedAddress);
        setErrors({});
      }
    } catch (_) {}
    // Run once on mount for initial props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load new Places library classes once available from Maps script
  useEffect(() => {
    let isMounted = true;
    const loadPlaces = async () => {
      try {
        // @ts-expect-error - importLibrary is available in modern Maps JS API
        const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary('places');
        if (!isMounted) return;
        placesLibRef.current = { Place, AutocompleteSessionToken, AutocompleteSuggestion };
        setPlacesLibLoaded(true);
        setSessionToken(new AutocompleteSessionToken());
      } catch (err) {
        console.error('Failed to import Places library', err);
      }
    };
    // google may not be ready if MapProvider not yet loaded
    if (typeof window !== 'undefined' && (window as any).google?.maps?.importLibrary) {
      loadPlaces();
    }
    return () => { isMounted = false; };
  }, []);

  // Fire onOpen once when the address form becomes visible (either by button click or autoOpen)
  useEffect(() => {
    try {
      if (showForm && !didOpenRef.current) {
        didOpenRef.current = true;
        onOpen?.();
      }
    } catch (_) {}
  }, [showForm, onOpen]);

  // Function to extract address components from either legacy PlaceResult or new Place
  const extractAddressComponents = useCallback((place: any): {
    streetName: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    latitude: number | null;
    longitude: number | null;
    countryCode: string | null;
    countryName: string | null;
  } => {
    let streetName = '';
    let houseNumber = '';
    let zipCode = '';
    let city = '';
    let countryCode: string | null = null;
    let countryName: string | null = null;
    // Latitude/Longitude: support both legacy geometry.location and new place.location
    let latitude: number | null = null;
    let longitude: number | null = null;

    const maybeGeometry = (place as any).geometry;
    const maybeLocation = (place as any).location;
    if (maybeGeometry?.location) {
      const loc = maybeGeometry.location;
      if (typeof loc.lat === 'function') {
        latitude = loc.lat();
        longitude = loc.lng();
      } else if (typeof loc.lat === 'number') {
        latitude = loc.lat;
        longitude = loc.lng;
      }
    } else if (maybeLocation) {
      const loc = maybeLocation;
      if (typeof loc.lat === 'function') {
        latitude = loc.lat();
        longitude = loc.lng();
      } else if (typeof loc.lat === 'number') {
        latitude = loc.lat;
        longitude = loc.lng;
      }
    }

    // Address components: legacy (address_components with long_name) or new (addressComponents with longText)
    const legacyComponents: any[] | undefined = (place as any).address_components;
    const newComponents: any[] | undefined = (place as any).addressComponents;
    const components = legacyComponents ?? newComponents ?? [];

    components.forEach((component: any) => {
      const types: string[] = component?.types ?? [];
      // Value fallback between legacy and new
      const longVal: string = component?.long_name ?? component?.longText ?? '';
      const shortVal: string = component?.short_name ?? component?.shortText ?? component?.shortName ?? '';
      if (types.includes('street_number')) {
        houseNumber = longVal;
      }
      if (types.includes('route')) {
        streetName = longVal;
      }
      if (types.includes('postal_code')) {
        zipCode = longVal;
      }
      if (types.includes('locality')) {
        city = longVal;
      } else if (types.includes('administrative_area_level_2') && !city) {
        city = longVal;
      } else if (types.includes('administrative_area_level_1') && !city) {
        city = longVal;
      }
      if (types.includes('country')) {
        countryName = longVal || countryName;
        countryCode = shortVal || countryCode;
      }
    });

    return { streetName, houseNumber, zipCode, city, latitude, longitude, countryCode, countryName };
  }, []);
  
  // Format suggestion text to include more detail (postal code + city when available)
  const formatSuggestionText = useCallback((suggestion: AutocompleteSuggestionType): { main: string, secondary: string } => {
    try {
      const placePrediction = suggestion?.placePrediction;
      const structuredFormat = placePrediction?.structuredFormat;
      
      if (structuredFormat) {
        const mainText = structuredFormat.mainText?.toString?.() ?? '';
        const secondaryText = structuredFormat.secondaryText?.toString?.() ?? '';
        return { main: mainText, secondary: secondaryText };
      }
      
      // Fallback to full text
      const fullText = placePrediction?.text?.toString?.() ?? '';
      return { main: fullText, secondary: '' };
    } catch (err) {
      return { main: suggestion?.placePrediction?.text?.toString?.() ?? '', secondary: '' };
    }
  }, []);
  
  // --- Autocomplete Handlers (new API) ---
  const fetchPredictions = useCallback(async (query: string) => {
    if (!placesLibLoaded || !placesLibRef.current) return [] as AutocompleteSuggestionType[];
    if (!sessionToken && placesLibRef.current?.AutocompleteSessionToken) {
      setSessionToken(new placesLibRef.current.AutocompleteSessionToken());
    }
    const { AutocompleteSuggestion } = placesLibRef.current;
    const request: any = {
      input: query,
      // Restrict/bias within Belgium roughly
      locationRestriction: { west: 2.5, south: 49.5, east: 6.4, north: 51.5 },
      // Include routes so that users typing street-only still get results
      includedPrimaryTypes: ['street_address', 'premise', 'route'],
    };
    if (sessionToken) {
      request.sessionToken = sessionToken;
    }
    try {
      setIsFetchingSuggestions(true);
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      // Sort so that suggestions containing a digit are listed first, but do not hide the rest
      const list = (suggestions as AutocompleteSuggestionType[]).slice();
      list.sort((a: any, b: any) => {
        const aHasNum = /\d/.test(a?.placePrediction?.text?.toString?.() ?? '');
        const bHasNum = /\d/.test(b?.placePrediction?.text?.toString?.() ?? '');
        return Number(bHasNum) - Number(aHasNum);
      });
      return list;
    } catch (err) {
      console.error('fetchAutocompleteSuggestions error', err);
      return [] as AutocompleteSuggestionType[];
    } finally {
      setIsFetchingSuggestions(false);
    }
  }, [placesLibLoaded, sessionToken]);

  // Debounce input changes to fetch predictions
  useEffect(() => {
    if (!placesLibLoaded) return;
    if (!addressInputValue || addressInputValue.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      const s = await fetchPredictions(addressInputValue.trim());
      setSuggestions(s);
      setHighlightedIndex(s.length > 0 ? 0 : -1);
    }, 300);
    return () => clearTimeout(handle);
  }, [addressInputValue, placesLibLoaded, fetchPredictions]);

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressInputValue(e.target.value);
    // If user types manually, invalidate the selection
    if (selectedPlace) {
      setSelectedPlace(null);
      setExtractedData(null);
      setErrors({ address: dictionary?.sellingForm?.selectFromList || 'Please select a valid address from the list.' });
    }
    // Clear specific address error if they start typing again
    if (errors.address) {
      setErrors(prev => ({ ...prev, address: undefined }));
    }
  };
  
  const handleInputFocus = () => {
    setIsInputFocused(true);
  };
  
  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    const container = inputContainerRef.current;
    if (container && next && container.contains(next)) {
      return;
    }
    // Close suggestions when focus leaves the input/suggestions container
    setIsInputFocused(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };
  
  const geocodeAddress = useCallback(async (address: string) => {
    if (!address) return null;
    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }

    return new Promise<google.maps.GeocoderResult | null>((resolve) => {
      geocoderRef.current?.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          resolve(results[0]);
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  const reverseGeocodeLocation = useCallback(async (lat: number | null, lng: number | null) => {
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      logDebug('Reverse geocode skipped due to invalid coordinates', { lat, lng });
      return null;
    }

    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }

    return new Promise<google.maps.GeocoderResult | null>((resolve) => {
      geocoderRef.current?.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          logDebug('Reverse geocode success', {
            lat,
            lng,
            formatted_address: results[0]?.formatted_address,
          });
          resolve(results[0]);
        } else {
          logDebug('Reverse geocode failed', { lat, lng, status, results });
          resolve(null);
        }
      });
    });
  }, [logDebug]);

  const fetchPlaceDetails = useCallback(async (placeId: string) => {
    if (!placeId) return null;
    try {
      if (!placesServiceRef.current) {
        placesServiceRef.current = new google.maps.places.PlacesService(document.createElement('div'));
      }

      return await new Promise<google.maps.places.PlaceResult | null>((resolve) => {
        placesServiceRef.current?.getDetails(
          {
            placeId,
            fields: ['formatted_address', 'address_components', 'addressComponents', 'geometry']
          },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place && place.geometry?.location) {
              resolve(place);
            } else {
              resolve(null);
            }
          }
        );
      });
    } catch (err) {
      console.error('Failed to fetch place details', err);
      return null;
    }
  }, []);

  const handleSelectSuggestion = useCallback(async (index: number) => {
    if (!placesLibLoaded || !placesLibRef.current) return;
    const suggestion = suggestions[index];
    if (!suggestion) return;
    
    const suggestionText = suggestion?.placePrediction?.text?.toString?.() ?? '';
    const suggestionMainText = suggestion?.placePrediction?.structuredFormat?.mainText?.toString?.() ?? '';
    const suggestionSecondaryText = suggestion?.placePrediction?.structuredFormat?.secondaryText?.toString?.() ?? '';
    const placeId = suggestion?.placePrediction?.placeId?.toString?.();
    console.groupCollapsed('[AddressForm] handleSelectSuggestion', suggestionText || suggestionMainText || `index-${index}`);
    logDebug('Raw suggestion payload', {
      suggestionText,
      suggestionMainText,
      suggestionSecondaryText,
      placeId,
      types: suggestion?.placePrediction?.types,
    });

    try {
      let resolvedPlace: any | null = null;

      const geocodeResult = await geocodeAddress(suggestionText);
      logDebug('Primary geocode result', geocodeResult);
      if (geocodeResult) {
        resolvedPlace = {
          address_components: geocodeResult.address_components,
          geometry: geocodeResult.geometry,
          formattedAddress: geocodeResult.formatted_address
        };
      }

      if (!resolvedPlace && placeId) {
        const placeDetails = await fetchPlaceDetails(placeId);
        logDebug('Place details result', placeDetails);
        if (placeDetails) {
          resolvedPlace = placeDetails;
        }
      }

      if (!resolvedPlace) {
        const fallbackAddress = [suggestionMainText, suggestionSecondaryText].filter(Boolean).join(', ');
        logDebug('Fallback address attempt', fallbackAddress || null);
        if (fallbackAddress) {
          const fallbackResult = await geocodeAddress(fallbackAddress);
          logDebug('Fallback geocode result', fallbackResult);
          if (fallbackResult) {
            resolvedPlace = {
              address_components: fallbackResult.address_components,
              geometry: fallbackResult.geometry,
              formattedAddress: fallbackResult.formatted_address
            };
          }
        }
      }

      if (!resolvedPlace || !resolvedPlace.geometry?.location) {
        const errorMessage = dictionary?.sellingForm?.invalidAddress || 'Unable to geocode address. Please try another selection.';
        logDebug('Failed to resolve place with usable geometry', {
          errorMessage,
          suggestionText,
          placeId,
        });
        showAddressError(errorMessage);
        onAddressError?.({
          code: 'geocode_failed',
          message: errorMessage,
          details: {
            suggestionText,
            suggestionMainText,
            suggestionSecondaryText,
            placeId
          }
        });
        return;
      }

      const extracted = extractAddressComponents(resolvedPlace);
      logDebug('Extracted components from resolved place', extracted);

      let resolvedStreetName = extracted.streetName;
      let resolvedHouseNumber = extracted.houseNumber;
      let resolvedZipCode = extracted.zipCode;
      let resolvedCity = extracted.city;
      const resolvedLatitude = extracted.latitude;
      const resolvedLongitude = extracted.longitude;
      let resolvedCountryCode = extracted.countryCode;
      let resolvedCountryName = extracted.countryName;

      // Backup: Extract house number from suggestion text if geocoding didn't give us one
      if (!resolvedHouseNumber && resolvedStreetName) {
        const textToSearch = suggestionMainText || suggestionText;
        const patterns = [
          new RegExp(`${resolvedStreetName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+[A-Za-z]?)`, 'i'),
          new RegExp(`(\\d+[A-Za-z]?)\\s+${resolvedStreetName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'),
          /\b(\d+[A-Za-z]?)\b/
        ];

        for (const pattern of patterns) {
          const match = textToSearch.match(pattern);
          if (match && match[1]) {
            resolvedHouseNumber = match[1];
            logDebug('House number recovered from suggestion text', {
              pattern: pattern.toString(),
              match: match[1],
            });
            break;
          }
        }
      }

      const hasLatitude = typeof resolvedLatitude === 'number' && !Number.isNaN(resolvedLatitude);
      const hasLongitude = typeof resolvedLongitude === 'number' && !Number.isNaN(resolvedLongitude);

      if (hasLatitude && hasLongitude && (!resolvedZipCode || !resolvedCity || !resolvedHouseNumber || !resolvedStreetName)) {
        logDebug('Attempting reverse geocode to fill missing address components', {
          resolvedStreetName,
          resolvedHouseNumber,
          resolvedZipCode,
          resolvedCity,
          resolvedLatitude,
          resolvedLongitude,
        });
        const reverseResult = await reverseGeocodeLocation(resolvedLatitude, resolvedLongitude);
        if (reverseResult) {
          const reverseExtracted = extractAddressComponents(reverseResult);
          logDebug('Reverse geocode extracted components', reverseExtracted);
          resolvedStreetName = resolvedStreetName || reverseExtracted.streetName;
          resolvedHouseNumber = resolvedHouseNumber || reverseExtracted.houseNumber;
          resolvedZipCode = resolvedZipCode || reverseExtracted.zipCode;
          resolvedCity = resolvedCity || reverseExtracted.city;
          resolvedCountryCode = resolvedCountryCode || reverseExtracted.countryCode;
          resolvedCountryName = resolvedCountryName || reverseExtracted.countryName;
        }
      }

      if (!resolvedCountryCode && resolvedZipCode && /^\d{4}$/.test(resolvedZipCode)) {
        resolvedCountryCode = 'BE';
        resolvedCountryName = resolvedCountryName || 'Belgium';
      }

      if (resolvedCountryCode && resolvedCountryCode.toUpperCase() !== 'BE') {
        const countryMessage = dictionary?.sellingForm?.invalidCountry || 'Selecteer een geldig adres in België.';
        logDebug('Rejected address outside Belgium', {
          suggestionText,
          resolvedCountryCode,
          resolvedCountryName,
          resolvedStreetName,
          resolvedZipCode,
        });
        showAddressError(countryMessage);
        onAddressError?.({
          code: 'country_not_supported',
          message: countryMessage,
          details: {
            suggestionText,
            suggestionMainText,
            suggestionSecondaryText,
            placeId,
            latitude: resolvedLatitude,
            longitude: resolvedLongitude,
            countryCode: resolvedCountryCode,
            countryName: resolvedCountryName,
          }
        });
        return;
      }

      if (!resolvedCountryCode) {
        const countryMessage = dictionary?.sellingForm?.invalidCountry || 'Selecteer een geldig adres in België.';
        logDebug('Country code unresolved after all attempts', {
          suggestionText,
          resolvedStreetName,
          resolvedZipCode,
          resolvedCity,
          resolvedLatitude,
          resolvedLongitude,
        });
        showAddressError(countryMessage);
        onAddressError?.({
          code: 'country_not_supported',
          message: countryMessage,
          details: {
            suggestionText,
            suggestionMainText,
            suggestionSecondaryText,
            placeId,
            latitude: resolvedLatitude,
            longitude: resolvedLongitude,
            resolvedZipCode,
            resolvedCity,
          }
        });
        return;
      }

      if (!resolvedZipCode) {
        const postalMessage = dictionary?.sellingForm?.postalCodeMissingMessage || 'We could not determine the postal code for this selection. Please choose a more specific address from the suggestions.';
        logDebug('Postal code unresolved after all attempts', {
          suggestionText,
          resolvedStreetName,
          resolvedHouseNumber,
          resolvedCity,
          resolvedLatitude,
          resolvedLongitude,
        });
        showAddressError(postalMessage);
        onAddressError?.({
          code: 'postal_code_unresolved',
          message: postalMessage,
          details: {
            suggestionText,
            suggestionMainText,
            suggestionSecondaryText,
            placeId,
            latitude: resolvedLatitude,
            longitude: resolvedLongitude,
          }
        });
        return;
      }

      if (!resolvedHouseNumber) {
        const fallbackHouseNumber = '1';
        logDebug('House number unresolved after all attempts, falling back to default', {
          fallbackHouseNumber,
          suggestionText,
        });
        onAddressError?.({
          code: 'house_number_unresolved',
          message: 'House number missing for selected address. Falling back to 1.',
          details: {
            suggestionText,
            suggestionMainText,
            suggestionSecondaryText,
            placeId,
            latitude: resolvedLatitude,
            longitude: resolvedLongitude,
          }
        });
        resolvedHouseNumber = fallbackHouseNumber;
      }

      if (!resolvedStreetName || !hasLatitude || !hasLongitude) {
        const missing = [] as string[];
        if (!resolvedStreetName) missing.push('street name');
        if (!hasLatitude || !hasLongitude) missing.push(dictionary?.sellingForm?.coordinates || 'coordinates');

        const message = dictionary?.sellingForm?.incompleteAddressDetails?.replace('{components}', missing.join(', ')) || `Incomplete address information (missing: ${missing.join(', ')}). Please select a more specific address from the list.`;
        showAddressError(message);
        onAddressError?.({
          code: 'missing_coordinates',
          message,
          details: {
            streetName: resolvedStreetName,
            city: resolvedCity,
            zipCode: resolvedZipCode,
            suggestionText,
            placeId
          }
        });
        return;
      }

      const finalLatitude = resolvedLatitude as number;
      const finalLongitude = resolvedLongitude as number;

      // Create a minimal place-like object for consistency
      const place = resolvedPlace;

      logDebug('Final resolved address components', {
        streetName: resolvedStreetName,
        houseNumber: resolvedHouseNumber,
        zipCode: resolvedZipCode,
        city: resolvedCity,
        countryCode: resolvedCountryCode,
        countryName: resolvedCountryName,
        latitude: finalLatitude,
        longitude: finalLongitude,
      });

      setSelectedPlace(place);
      // Store our extracted and validated values
      setExtractedData({ 
        houseNumber: resolvedHouseNumber, 
        zipCode: resolvedZipCode,
        latitude: finalLatitude,
        longitude: finalLongitude,
        countryCode: resolvedCountryCode,
      });
      
      // Build display text
      let inputText: string;
      if (resolvedHouseNumber && resolvedStreetName) {
        const parts = [`${resolvedStreetName} ${resolvedHouseNumber}`];
        if (resolvedZipCode && resolvedCity) parts.push(`${resolvedZipCode} ${resolvedCity}`);
        else if (resolvedCity) parts.push(resolvedCity);
        else if (resolvedZipCode) parts.push(resolvedZipCode);
        inputText = parts.join(', ');
      } else {
        inputText = place.formattedAddress ?? place.formatted_address ?? suggestionText;
      }
      logDebug('Input text after selection', inputText);
      
      setAddressInputValue(inputText);
      setSuggestions([]);
      setHighlightedIndex(-1);
      setErrors({});
      
      // Reset session token
      if (placesLibRef.current?.AutocompleteSessionToken) {
        setSessionToken(new placesLibRef.current.AutocompleteSessionToken());
      }
      setIsInputFocused(false);
      inputRef.current?.blur();
    } catch (err) {
      console.error('Failed to geocode address', err);
      const message = dictionary?.sellingForm?.invalidAddress || 'Please select a valid address from the list.';
      showAddressError(message);
      onAddressError?.({
        code: 'geocode_failed',
        message,
        details: {
          suggestionText,
          suggestionMainText,
          suggestionSecondaryText,
          placeId,
          error: err instanceof Error ? err.message : String(err)
        }
      });
    } finally {
      console.groupEnd();
    }
  }, [suggestions, placesLibLoaded, dictionary, extractAddressComponents, geocodeAddress, fetchPlaceDetails, reverseGeocodeLocation, onAddressError, logDebug, showAddressError]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        void handleSelectSuggestion(highlightedIndex);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };
  // --- End Autocomplete Handlers ---

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: typeof errors = {};

    // Validate that a place was selected
    if (!selectedPlace || !extractedData) {
      showAddressError(dictionary?.sellingForm?.selectFromList || 'Please select a valid address from the list.');
      return;
    }
    
    // Extract base components from place, but use our fixed/extracted values
    const baseComponents = extractAddressComponents(selectedPlace);
    const { houseNumber, zipCode, latitude, longitude, countryCode } = extractedData; // Use our corrected values
    const { streetName, city } = baseComponents;

    if (countryCode && countryCode.toUpperCase() !== 'BE') {
      const countryMessage = dictionary?.sellingForm?.invalidCountry || 'Selecteer een geldig adres in België.';
      showAddressError(countryMessage);
      return;
    }
    
    // Validate property type
    const formData = new FormData(e.currentTarget);
    const propertyTypeValue = formData.get('propertyType') as 'apartment' | 'house';
    if (!propertyTypeValue) {
      newErrors.propertyType = dictionary?.sellingForm?.requiredField || 'Property type is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit({ 
      streetName,
      houseNumber, 
      zipCode, 
      city, 
      propertyType: propertyTypeValue,
      latitude,
      longitude
    });
  };
  
  return (
    <>
      <div className="w-full">
        <p className="text-base md:text-sm text-zinc-800 pb-3">
          {dictionary?.sellingForm?.addressIntroBody ||
            'I\'ll ask a few quick questions so we can match your home with recent nearby comparables and compute a fair value. We use official data and AI to analyze your property and neighborhood.'}
        </p>
        {!showForm && (
          <div className="pt-1 pb-2">
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1 bg-white text-zinc-800 hover:text-zinc-950 font-semibold text-base md:text-sm focus:outline-none border border-zinc-800 px-3 py-1 rounded-md hover:bg-zinc-50 transition-colors"
            >
              {dictionary?.sellingForm?.addressIntroCTA || 'Start Step 1 — Address'}
              <ChevronDownIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="w-full">
          <p className="text-base md:text-sm text-zinc-800 pb-2 pt-3 border-t border-zinc-200">
            {/* Update instruction text */}
            {dictionary?.sellingForm?.addressAutocompleteMessage || 'Enter the address of the property:'}
          </p>
        
          {/* --- Autocomplete Input (new predictions API) --- */}
          <div className="mb-2 relative" ref={inputContainerRef} onBlur={handleContainerBlur}> 
            <input
              type="text"
              name="address" // Keep name for potential form data access if needed elsewhere
              placeholder={dictionary?.sellingForm?.addressPlaceholder || 'Start typing address...'}
              className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
              value={addressInputValue}
              onChange={handleAddressInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              autoComplete="street-address"
              ref={inputRef}
            />
            {(isInputFocused && suggestions.length > 0) && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-zinc-300 rounded-lg shadow-sm overflow-hidden">
                <ul className="max-h-60 overflow-auto">
                  {suggestions.map((sugg, idx) => {
                    const { main, secondary } = formatSuggestionText(sugg);
                    return (
                      <li
                        key={idx}
                        className={`px-3 py-2 cursor-pointer ${idx === highlightedIndex ? 'bg-zinc-100' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); }}
                        onClick={() => void handleSelectSuggestion(idx)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                      >
                        <div className="text-sm text-zinc-900 font-medium">{main}</div>
                        {secondary && (
                          <div className="text-xs text-zinc-600 mt-0.5">{secondary}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-zinc-200">
                  <span className="text-[10px] text-zinc-500">Powered by</span>
                  <Image
                    src="https://maps.gstatic.com/mapfiles/api-3/images/google_gray.svg"
                    alt="Google"
                    width={20}
                    height={20}
                    className="h-5 w-5"
                    onMouseDown={(e) => e.preventDefault()}
                  />
                </div>
              </div>
            )}
            {/* Display Address Error */}
            {errors.address && <p className="text-red-600 text-sm mt-1">{errors.address}</p>}
          </div>
          {/* --- End Autocomplete Input --- */}

          {/* --- Conditional Rendering for Property Type & Submit --- */}
          {/* Show property type selection only after a valid address is selected */}
          {selectedPlace && (
            <>
              <div className="mt-2"> {/* Add some margin top */}
                <p className="text-base md:text-sm text-zinc-800 mb-2">
                  {dictionary?.sellingForm?.propertyTypeQuestion || 'What type of property is it?'}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="propertyTypeApartment" className="flex-1 flex items-center justify-center cursor-pointer p-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                    <input
                      type="radio"
                      id="propertyTypeApartment"
                      name="propertyType" // Ensure name matches for FormData access
                      value="apartment"
                      checked={propertyType === 'apartment'}
                      onChange={() => setPropertyType('apartment')}
                      className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900 sr-only"
                    />
                    <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-2">
                      {dictionary?.sellingForm?.propertyTypeApartment || 'Apartment'}
                      {propertyType === 'apartment' && (
                        <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                  </label>

                  <label htmlFor="propertyTypeHouse" className="flex-1 flex items-center justify-center cursor-pointer p-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                    <input
                      type="radio"
                      id="propertyTypeHouse"
                      name="propertyType" // Ensure name matches for FormData access
                      value="house"
                      checked={propertyType === 'house'}
                      onChange={() => setPropertyType('house')}
                      className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900 sr-only"
                    />
                    <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-2">
                      {dictionary?.sellingForm?.propertyTypeHouse || 'House'}
                      {propertyType === 'house' && (
                        <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                  </label>
                </div>
                {errors.propertyType && <p className="text-red-600 text-sm mt-1">{errors.propertyType}</p>}
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  // Disable submit if disabled or Places library not yet ready
                  disabled={disableSubmit || !placesLibLoaded} 
                  className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {dictionary?.sellingForm?.continueButton || 'Continue'}
                </button>
              </div>
            </>
          )}
          {/* --- End Conditional Rendering --- */}

        </form>
      )}

      {/* Removed old style tag as it's likely not needed */}
    </>
  );
}
